import React from 'react';
import { Search, X } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface LeadsToolbarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  statusFilter: string;
  onStatusChange: (value: string) => void;
  platformFilter: string;
  onPlatformChange: (value: string) => void;
  priorityFilter: string;
  onPriorityChange: (value: string) => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
}

const STATUS_PILLS = [
  { id: 'all', label: 'All Leads', dot: 'bg-primary' },
  { id: 'new', label: 'New / Uncontacted', dot: 'bg-sky-500' },
  { id: 'contacted', label: 'In Progress', dot: 'bg-amber-500' },
  { id: 'qualified', label: 'Qualified', dot: 'bg-emerald-500' },
  { id: 'converted', label: 'Converted', dot: 'bg-indigo-500' },
];

export function LeadsToolbar({
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusChange,
  platformFilter,
  onPlatformChange,
  priorityFilter,
  onPriorityChange,
  hasActiveFilters,
  onClearFilters,
}: LeadsToolbarProps) {
  return (
    <div className="space-y-2.5">
      {/* ServerlessLens-style Status Filter Pills Strip */}
      <div className="flex flex-wrap items-center gap-1.5 overflow-x-auto pb-0.5">
        {STATUS_PILLS.map((pill) => {
          const isActive = statusFilter === pill.id;
          return (
            <button
              key={pill.id}
              type="button"
              onClick={() => onStatusChange(pill.id)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-all duration-150',
                isActive
                  ? 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 shadow-xs border border-slate-900 dark:border-slate-100'
                  : 'bg-white/90 dark:bg-slate-850 text-slate-600 dark:text-slate-300 border border-slate-200/80 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 hover:bg-white dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white shadow-2xs'
              )}
            >
              <span
                className={cn(
                  'h-1.5 w-1.5 rounded-full',
                  isActive ? 'bg-emerald-400' : pill.dot
                )}
              />
              <span>{pill.label}</span>
            </button>
          );
        })}
      </div>

      {/* Main Search & Dropdown Filter Row */}
      <Card className="max-w-none p-2.5 sm:p-3 border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xs rounded-2xl">
        <div className="flex flex-col gap-2.5 md:flex-row md:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-slate-500" aria-hidden="true" />
            <Input
              placeholder="Search by name, customer message, or product inquiry…"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              className="h-9 pl-10 rounded-full border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-850 text-xs text-slate-800 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:bg-white dark:focus:bg-slate-900 focus:ring-primary/30 focus:border-primary transition-colors shadow-2xs"
              aria-label="Search leads"
            />
          </div>

          <div className="grid grid-cols-3 gap-2 md:flex md:w-auto md:shrink-0">
            <Select
              value={statusFilter}
              onChange={(e) => onStatusChange(e.target.value)}
              className="h-9 text-xs rounded-xl border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-850 text-slate-800 dark:text-slate-200 md:w-32 focus:bg-white dark:focus:bg-slate-900"
              aria-label="Filter by status"
            >
              <option value="all">All status</option>
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="qualified">Qualified</option>
              <option value="converted">Converted</option>
              <option value="closed">Closed</option>
            </Select>

            <Select
              value={platformFilter}
              onChange={(e) => onPlatformChange(e.target.value)}
              className="h-9 text-xs rounded-xl border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-850 text-slate-800 dark:text-slate-200 md:w-34 focus:bg-white dark:focus:bg-slate-900"
              aria-label="Filter by platform"
            >
              <option value="all">All platforms</option>
              <option value="facebook">Facebook</option>
              <option value="instagram">Instagram</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="website">Website</option>
            </Select>

            <Select
              value={priorityFilter}
              onChange={(e) => onPriorityChange(e.target.value)}
              className="h-9 text-xs rounded-xl border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-850 text-slate-800 dark:text-slate-200 md:w-30 focus:bg-white dark:focus:bg-slate-900"
              aria-label="Filter by priority"
            >
              <option value="all">All priority</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </Select>
          </div>

          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              className="h-9 shrink-0 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white rounded-xl"
            >
              <X className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
              Clear
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}
